import React, { Fragment, useEffect, useState } from 'react';
import { GenericButton } from 'components/GenericButton';
import { GenericCheckbox } from 'components/GenericCheckbox';
import { GenericTextInput } from 'components/GenericTextInput';
import { GenericTooltip } from 'components/GenericTooltip';
import { createNewProfile } from 'containers/ProfileManager/redux/profile.actions';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import { useSelector, useDispatch } from 'react-redux';
import { getTranslation } from 'utils/getTranslation';
import { ProfileImageOption } from '../ProfileImageOption';
import { ProfilesHeader } from '../ProfilesHeader';
import { KidsTooltipContent } from '../KidsTooltipContent';
import { getStatusControlPin } from 'containers/EPG/actions/channelBlocker/blocker.actions';
import { hasPinConfigured } from 'containers/EPG/selectors/pin.selectors';
import { useProfileForm } from 'containers/ProfileManager/hooks/useProfileForm';
import {
  hidePinForm,
  showPinForm,
} from 'containers/PinForm/actions/pinForm.actions';
import { getCurrentUserRole } from 'containers/Profiles/selectors';
import { CustomAlert } from 'components/CustomAlert';
import './style.scss';
import { MobileGenericHeader } from 'components/MobileGenericHeader';
import { SET_PROFILE_FORM_FIELD } from 'containers/ProfileManager/redux/profile.types';

export const CreateProfileView = () => {
  const {
    configProfileForm: { user_image }, avatars
  } = useSelector(state => state.profilesManager);
  const [displayAlert, setDisplayAlert] = useState(false);
  const dispatch = useDispatch();
  const { isMobile } = useVerifyIsMobile(768);
  const statusPin = useSelector(hasPinConfigured);
  const translations = useSelector(state => state.translations);
  const isAdmin = useSelector(state => getCurrentUserRole(state));
  const asset = useSelector(state => state.asset);
  const {
    handleCancelClick,
    handleChange,
    handleCheckChange,
    profileForm,
    setFormAction,
    showTooltip,
    setShowTooltip,
    nameLength,
  } = useProfileForm({ isEditing: false });

  useEffect(() => {
    !statusPin && dispatch(getStatusControlPin());
  }, []);

  const createProfileAction = () =>
    dispatch(createNewProfile({ ...profileForm, user_image: user_image || avatars.at(-1)?.user_image }));

  const handleCreateProfile = () => {
    showTooltip && setShowTooltip(false);
    if (!profileForm.is_kids) {
      return createProfileAction();
    }

    if (statusPin) {
      return dispatch(
        showPinForm({
          type: 'UNBLOCK_CHANNEL',
          title: getTranslation(
            'lockPIN_access_subtitleKids_label',
            translations,
          ),
          onSuccess: async () => {
            await createProfileAction();
            await dispatch(hidePinForm());
          },
        }),
      );
    }

    if (isAdmin) {
      dispatch({ type: SET_PROFILE_FORM_FIELD, payload: profileForm });
      return dispatch(
        showPinForm({
          type: 'DECIDE_NEW_PIN',
          title: getTranslation(
            'setupPin_confirmation_title_label',
            translations,
          ),
          onSuccess: async () => {
            await createProfileAction();
            await dispatch(hidePinForm());
          },
        }),
      );
    }

    return setDisplayAlert(true);
  };

  return (
    <Fragment>
      <section className="create-profile-view profile-config">
        {!isMobile && <ProfilesHeader />}
        {isMobile ? (
          <MobileGenericHeader
            onBack={handleCancelClick}
            caption={getTranslation(
              'addProfile_access_title_label',
              translations,
            )}
          />
        ) : (
          <h2 className="create-profile-view__title profile-config__title">
            {getTranslation('addProfile_access_title_label', translations)}
          </h2>
        )}
        <div className="create-profile-view__form profile-config__form">
          <ProfileImageOption
            profileImg={user_image}
            onOptionClick={setFormAction}
          />
          <GenericTextInput
            classNames={['create-profile-view__form__input']}
            caption={getTranslation(
              'addProfile_access_nameProfile_label',
              translations,
            )}
            onChange={handleChange}
            value={profileForm.firstname}
            name="profile-name"
            styles={{ marginBottom: 29 }}
            textInputProps={{
              autoFocus: true,
            }}
          />
          <div className="create-profile-view__form__check">
            <GenericCheckbox
              checked={profileForm.is_kids}
              id={'is_kids'}
              caption={getTranslation(
                'addProfile_access_checkKids_label',
                translations,
              )}
              onCheckChange={handleCheckChange}
            />
            <GenericTooltip
              onCloseTooltip={() => setShowTooltip(false)}
              showTooltip={showTooltip}
              timeToHide={14}
              classNames={['profiles-kids-tooltip']}
            >
              <KidsTooltipContent />
            </GenericTooltip>
          </div>
          <GenericButton
            textContent={getTranslation(
              'addProfile_access_option_button_save',
              translations,
            )}
            type="primary"
            onClick={handleCreateProfile}
            disabled={
              profileForm.firstname.length === 0 ||
              profileForm.firstname.length < nameLength.minLength
            }
            styles={{ marginBottom: 16, marginTop: 155 }}
          />
          <GenericButton
            textContent={getTranslation(
              'addProfile_access_option_button_cancel',
              translations,
            )}
            type="secondary"
            onClick={handleCancelClick}
          />
        </div>
      </section>
      {displayAlert && (
        <CustomAlert
          text={getTranslation(
            'addProfile_pinAlert_description_label',
            translations,
          )}
          textButton={getTranslation(
            'addProfile_pinAlert_option_button_ok',
            translations,
          )}
          onClose={() => setDisplayAlert(false)}
          asset={asset.avatarSelector_access_icon_error}
        />
      )}
    </Fragment>
  );
};
